import { NavLink } from "react-router";
import { DuviNicheSiteBrandMark } from "@/components/duvi-niche-site-brand-mark";
import { DuviNicheSiteButton } from "@/components/duvi-niche-site-button";

export interface DuviNicheSiteNavBarLink {
  label: string;
  /** Same-site route. */
  to: string;
  /** Exact-match the route (used for `/`, which otherwise matches everything). */
  end?: boolean;
}

export interface DuviNicheSiteNavBarProps {
  links: DuviNicheSiteNavBarLink[];
  /** Sign-up CTA target — business registration on `app.<niche-domain>`. */
  registerUrl: string;
  ctaLabel?: string;
}

/**
 * DuviNicheSiteNavBar — ported from the bespoke Fishwalkers design system's
 * `components/marketing/NavBar.jsx`
 * (duvi-design/exports/niches/fishwalkers/system.zip): the brand mark, the
 * primary nav, and one primary CTA on a 95%-canvas scrim with a 6px backdrop
 * blur. This is the system's ONLY sticky element and one of only two places
 * it permits transparency.
 *
 * The export drives `activeHref` from a prop because the prototype is two
 * hash-anchored artboards on one canvas; the real site is two routes, so
 * active state comes from the router's own `NavLink` match instead. The
 * rendered classes are the export's.
 */
export function DuviNicheSiteNavBar({
  links,
  registerUrl,
  ctaLabel = "Sign up",
}: DuviNicheSiteNavBarProps) {
  return (
    <header className="sticky top-0 z-50 w-full border-b border-[var(--border)] bg-[var(--nav-scrim)] shadow-[var(--shadow-nav)] backdrop-blur-[6px]">
      <div className="mx-auto flex w-full max-w-[var(--container-max)] items-center justify-between gap-[var(--space-6)] px-[var(--container-pad)] py-[var(--space-4)] lg:px-[var(--container-pad-lg)]">
        <DuviNicheSiteBrandMark size={34} to="/" />
        {/* The export hides the nav below `md` because its prototype has a
            mobile menu drawn nowhere. With two short links this site keeps
            them visible at every width rather than stranding a phone reader
            with no route to /pricing — the same links today's site shows. */}
        <nav aria-label="Main">
          <ul className="m-0 flex list-none items-center gap-[var(--space-5)] p-0 sm:gap-[var(--space-8)]">
            {links.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    [
                      "font-[family-name:var(--font-body)] text-[15px] font-medium no-underline transition-colors duration-[var(--duration-fast)]",
                      isActive
                        ? "text-[color:var(--brand)]"
                        : "text-[color:var(--body)] hover:text-[color:var(--brand)]",
                    ].join(" ")
                  }
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="flex items-center gap-[var(--space-3)]">
          <DuviNicheSiteButton href={registerUrl}>
            {ctaLabel}
          </DuviNicheSiteButton>
        </div>
      </div>
    </header>
  );
}
